import './mail-askForHelp-actions.ajscss';

import { AgentDesktopRouter } from '@agent/agent-interface/agent-desktop-router';
import { AbstractEpticaController } from '@agent/agent-interface/contact-view/mail/abstract-mail-process';
import { IEpticaDeprecatedService } from '@agent/core/channel-digit/mail/eptica/eptica-deprecated.service';
import { EpticaData } from '@agent/core/channel-digit/mail/eptica/models/eptica-data';
import {
  HOME_PAGE_NAME,
  IEpticaRequestFrame,
  REQUEST_PAGE_NAME,
} from '@agent/core/channel-digit/mail/eptica/models/eptica.request.interfaces';
import { IContactDeprecatedService } from '@agent/core/contact/contact-deprecated.service';
import { ITransfer, TransferKey, TransferTarget } from '@agent/core/contact/transfer/models/transfer';
import { ITransferDeprecatedService } from '@agent/core/contact/transfer/transfer-deprecated.service';
import { ToastDeprecatedService } from '@commons/core/notification/toast-deprecated.service';
import { IEpticaExtractAttachmentFrom } from '@commons/history/history-mail/models/eptica-extract';
import { IContact } from '@commons/models/contact';
import { ng1Destroy } from '@commons/util/utilities/rx';
import { IComponentController, IComponentOptions, ILogService, IPromise, IQService } from 'angular';

import { BookingMailFacade, ChannelType, ProfileFacade, TranslateService } from '@contact/upgrade';

import templateUrl from './mail-askForHelp-actions.template.html';
import { Alert } from '@agent/core/alert/models/alert';
import { EpticaHelperJson } from '@agent/core/channel-digit/mail/eptica/eptica.helper.json';

class MailAskForHelpActionsCtrl extends AbstractEpticaController<IEpticaRequestFrame> implements IComponentController {
  override responseId: string;
  private enableValidateBtn = true;
  private requestPromise: IPromise<IEpticaRequestFrame>;
  private transferKey: TransferKey;
  private profileId: string;

  constructor(
    override readonly $log: ILogService,
    override readonly $q: IQService,
    private readonly transferDeprecatedService: ITransferDeprecatedService,
    override readonly contactDeprecatedService: IContactDeprecatedService,
    private readonly epticaDeprecatedService: IEpticaDeprecatedService,
    override readonly toastDeprecatedService: ToastDeprecatedService,
    private readonly profileFacade: ProfileFacade,
    private readonly bookingMailFacade: BookingMailFacade,
    override readonly router: AgentDesktopRouter,
    override readonly translateService: TranslateService
  ) {
    super($log, $q, contactDeprecatedService, toastDeprecatedService, router, translateService);
  }

  get canValidate(): boolean {
    return (
      this.enableValidateBtn &&
      this.transferDeprecatedService.canValidateTransfer(this.transferKey, ChannelType.MAIL) &&
      this.transferDeprecatedService.validateSkillAndAgentForAssistance(this.transferKey, ChannelType.MAIL)
    );
  }

  validate(): void {
    this.contactDeprecatedService.getContact(this.responseId).then(contact => {
      this.enableValidateBtn = false;
      this.transferDeprecatedService.getTransfer(this.transferKey).map((transfer: ITransfer) => {
        switch (transfer.targetType) {
          case TransferTarget.SKILL:
            this.askForHelpToAgent(contact.epticaRequestId || 0, transfer);
            break;
          case TransferTarget.AGENT:
          case TransferTarget.AGENT_ASSISTANCE_BY_SKILL:
            this.askForHelpToAgent(contact.epticaRequestId || 0, transfer);
            break;
          case TransferTarget.CORRESPONDENT:
            this.askForHelpCorrespondent(contact.epticaRequestId || 0, transfer);
            break;
          case TransferTarget.FREE_NUMBER:
            this.askForHelpFreeMail(contact.epticaRequestId || 0, transfer);
            break;
          default:
        }
      });
    });
  }

  askForHelpCorrespondent(epticaRequestId: number, transfer: ITransfer): void {
    this.askForHelpToEmail(epticaRequestId, transfer.target.email, transfer.comment);
  }

  askForHelpFreeMail(epticaRequestId: number, transfer: ITransfer): void {
    this.askForHelpToEmail(epticaRequestId, transfer.target, transfer.comment);
  }

  askForHelpToEmail(epticaRequestId: number, email: string, comment: string): void {
    this.handleJsonAction(request => {
      request.fillAssistanceToEmail(email, comment);
      return request.assist(epticaRequestId);
    }, 'ASK_FOR_HELP');
  }

  askForHelpToAgent(epticaRequestId: number, transfer: ITransfer): void {
    this.bookingMailFacade
      .askForHelpToAgent(this.responseId, {
        param: {
          target: transfer.target.uuid,
          comment: transfer.comment,
          blind: !!transfer.blind,
        },
        attachments: this.epticaDeprecatedService
          .getAttachmentFrom(`${epticaRequestId}`, IEpticaExtractAttachmentFrom.ASK_FOR_HELP)
          .map(attachment => attachment.file),
      })
      .afterEvent$.pipe(ng1Destroy(this))
      .subscribe(() =>
        this.mainPromise().then(r => {
          r.finish();
          EpticaHelperJson.displayActionMessages(this.toastDeprecatedService, {
            messageState: 'success',
            messageLabel: this.translateService.instant('agent:contact.workspace.basket.request_type_ask_for_help_sent_success'),
          });
        })
      );
  }

  cancel(): void {
    // remove PJs if added when canceling and then go back to process state
    this.mainPromise().then(request => {
      this.epticaDeprecatedService.getAttachmentFrom(`${request.getId()}`, IEpticaExtractAttachmentFrom.ASK_FOR_HELP).forEach(a => {
        request.removeAttachmentFileForAskForHelp(a);
        this.epticaDeprecatedService.removeAttachment(`${request.getId()}`, a);
      });

      this.transferDeprecatedService.cancelTransfer(this.transferKey).then(() => {
        this.enableValidateBtn = true;
        request.toggleAnswerPanel(true);
        request.toggleAssistPanel(false);
        this.router.navigateFromLastChild(['..']);
      });
    });
  }

  override $onInit(): void {
    super.$onInit();
    this.requestPromise = this.contactDeprecatedService.getContact(this.responseId).then(contact =>
      this.epticaDeprecatedService.loadRequest({
        requestId: contact.epticaRequestId || 0,
        responseId: this.responseId,
      })
    );

    this.mainPromise().then(request => {
      request.toggleAssistPanel(true);
      // compute transfer key
      this.transferKey = [request.getId()];
      // set target skill by default
      this.transferDeprecatedService.getTransfer(this.transferKey).map((transfer: ITransfer) => {
        transfer.targetType = TransferTarget.AGENT;
        transfer.comment = '';
      });
    });

    this.profileFacade.id$.pipe(ng1Destroy(this)).subscribe(id => (this.profileId = id));
  }

  checkEpticaDataAfterReload(request: IEpticaRequestFrame, epticaData: EpticaData): IPromise<EpticaData> {
    if (epticaData.pageName === HOME_PAGE_NAME) {
      this.endContact(this.responseId).finally(() => request.finish());
    } else if (epticaData.pageName === REQUEST_PAGE_NAME) {
      // Show the request
      request.show();
    } else {
      this.contactDeprecatedService.cancel(this.responseId).finally(() => this.mainPromise().then(r => r.finish()));
    }
    return this.$q.resolve(epticaData);
  }

  mainPromise(): IPromise<IEpticaRequestFrame> {
    return this.requestPromise;
  }

  /**
   * End the contact normally
   */
  protected override endContact(responseId: string): IPromise<IContact> {
    return this.contactDeprecatedService
      .getContact(responseId)
      .then(() => this.contactDeprecatedService.refuse(this.responseId, 'ASK_FOR_HELP'));
  }
}

export class MailAskForHelpActions implements IComponentOptions {
  templateUrl = templateUrl;
  bindings = {
    responseId: '<',
  } as { [binding: string]: string };
  controller = [
    '$log',
    '$q',
    'transferDeprecatedService',
    'contactDeprecatedService',
    'epticaDeprecatedService',
    'toastDeprecatedService',
    'profileFacade',
    'bookingMailFacade',
    'agentDesktopRouter',
    'translateService',
    MailAskForHelpActionsCtrl,
  ];
}

export default 'contactMailAskForHelpActions';
